import { createSessionCache } from './session-cache.js'

interface MultiBoxDiscount {
  box: number
  discount: number
}

export interface ClientCoupon {
  coupon_code: string
  discount: number
  coupon_type: 'refurCoupon' | 'multiuseCoupon'
  is_partner_coupon: 0 | 1
  referrer: {
    uuid: string
  } | null
  discount_amount_type: 'percentage' | 'dollar'
  multi_box_discount_enabled: 0 | 1
  multi_box_discounts: ReadonlyArray<MultiBoxDiscount> | null
  validate_message: string
  firstname?: string
}

interface CouponResponse {
  data: ClientCoupon | []
}

interface CouponOptions {
  apiUrl?: string
  fallback?: string
}

const DEFAULT_API_URL = 'https://production.api.lyka.com.au/api/v1/'

// Shared cookie used to keep the active coupon in sync across www, journal, build-a-box,
// and lyka-coupon-banner (and other apps not in this repo).
const COUPON_COOKIE_NAME = 'coupon'
const COUPON_COOKIE_MAX_AGE_SECONDS = 24 * 60 * 60 // 1 day, matches build-a-box

export function readCouponCookie(): string | undefined {
  if (typeof document === 'undefined') return undefined
  const match = document.cookie.split('; ').find((row) => row.startsWith(`${COUPON_COOKIE_NAME}=`))
  return match?.split('=')[1] || undefined
}

export function writeCouponCookie(code: string): void {
  if (typeof document === 'undefined') return
  document.cookie = [
    `${COUPON_COOKIE_NAME}=${code}`,
    `Domain=${location.hostname}`,
    'Path=/',
    'Secure',
    `Max-Age=${COUPON_COOKIE_MAX_AGE_SECONDS}`,
  ].join('; ')
}

const load = createSessionCache<ClientCoupon | null>({
  windowKey: '__lyka_coupon_cache__',
  sessionPrefix: '__lyka_coupon_session__',
})

function resolveApiUrl(apiUrl?: string): string {
  const resolved = apiUrl || import.meta.env.PUBLIC_LYKA_API_URL || import.meta.env.VITE_LYKA_API_URL || DEFAULT_API_URL

  return resolved.endsWith('/') ? resolved : `${resolved}/`
}

async function fetchCoupon(code: string, apiUrl: string): Promise<ClientCoupon | null> {
  const response = await fetch(new URL('coupons/validate', apiUrl), {
    method: 'POST',
    mode: 'cors',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ couponCode: code }),
  })

  if (!response.ok) {
    throw new Error(`Coupon request failed with status ${response.status}`)
  }

  const { data } = (await response.json()) as CouponResponse

  // If the backend returns an array it means no coupon was found matching
  return Array.isArray(data) ? null : data
}

export async function loadClientCoupon(code: string, options?: CouponOptions): Promise<ClientCoupon | undefined> {
  const trimmedCode = code.trim()

  if (!trimmedCode) {
    return undefined
  }

  const apiUrl = resolveApiUrl(options?.apiUrl)
  const cacheKey = `${apiUrl}::${trimmedCode}`
  const result = await load(cacheKey, () => fetchCoupon(trimmedCode, apiUrl)).catch((error) => {
    console.error('Error fetching coupon details:', error)
    return undefined
  })

  if (result) return result

  // Fallback to the default coupon if the provided coupon fails to load
  // (invalid coupon etc)
  const fallback = options?.fallback?.trim()
  if (fallback && fallback !== trimmedCode) {
    return loadClientCoupon(fallback, { apiUrl: options?.apiUrl })
  }

  return undefined
}
